/**
 * Pure-data English catalogue for namespace 'cmd' (W1-10a core-commands-structure; imports only core/util/types).
 * Failure reasons (`tx.fail`) and notes (`tx.note`) of the structure command bodies: notes, lines, line types,
 * parents, text segments, tempo and timing, chart metadata, package settings, speed modes, assets, effects, videos
 * and chart merging. Keys are written WITHOUT the namespace prefix; code produces MessageRefs such as
 * `{ key: 'cmd.notes.uiMover' }`. Parameters are language-neutral (ids, numbers, names, paths, beats, codes).
 */

import type { MessageCatalog } from '../util/types';

/** A note field id. */
const NOTE_FIELD =
  '{field, select, x {Position X} speed {Speed} yOffset {Height offset} size {Width} alpha {Opacity} visibleTime {Appear time} judgeArea {Judge width} tint {Tint} tintHitEffects {Hit effect tint} hitsound {Hit sound} above {Side} fake {Fake} other {{field}}}';

/** A package setting id. */
const PKG_FIELD =
  '{field, select, aspectRatio {The aspect ratio must be a number greater than 0.} backgroundDim {The background dim must be between 0 and 1.} lineLength {The line length must be a number greater than 0.} speedBakeTolPx {The speed baking tolerance must be a number greater than 0.} rpeVersion {RPEVersion must be a whole number of 0 or more.} infoOffsetSec {The info.yml offset must be a finite number of seconds.} other {The setting {field} has an invalid value.}}';

/** A HUD element id (attachUI). */
const ELEMENT =
  '{element, select, pause {Pause button} combonumber {Combo number} combo {COMBO label} score {Score} bar {Progress bar} name {Song name} level {Level} other {{element}}}';

/** Asset path problem codes (model `AssetPathProblem`). */
const PATH_REASON =
  '{reason, select, empty {the path is empty} leadingSlash {it starts with "/"} backslash {it contains a backslash} emptyFolder {it has an empty folder name} dotSegment {it contains "." or ".." as a folder} reserved {Phira reserves this name} clickPng {Phira treats files ending in click.png as skin files} builtinHitsound {Phira plays its own sound for this hit sound name} other {{reason}}}';

const catalog: MessageCatalog = {
  ns: 'cmd',
  locale: 'en',
  messages: {
    // Generic
    badBeat: 'That beat is not a valid position.',
    'missing.line': 'This line no longer exists.',
    'missing.note': 'This note no longer exists.',
    'missing.key': 'This keyframe no longer exists.',
    'missing.track': 'This property track no longer exists.',
    'missing.group': 'This line group no longer exists.',
    'missing.marker': 'This marker no longer exists.',
    'missing.bpm': 'This tempo point no longer exists.',
    'missing.effect': 'This effect no longer exists.',
    'missing.video': 'This video no longer exists.',
    'missing.asset': 'The file this refers to is not in the project.',

    // Notes
    'notes.uiMover': `Notes on this line would be invisible: "{line}" moves the ${ELEMENT} display. Put the notes on another line.`,
    'notes.badField': `${NOTE_FIELD} has an invalid value.`,
    'notes.beforeStart': 'Notes cannot be placed or moved before beat 0.',
    'notes.holdEnd': 'A hold must end after it starts.',
    'notes.holdLength': 'A hold needs a length greater than zero.',
    'notes.exprFailed': 'The expression gives no valid number for note {index} of {count}.',
    'notes.gridCount': 'The X grid needs between 2 and 1351 lines.',
    'notes.snapDiv': 'The beat grid must divide a beat into 1 to 1920 parts.',
    'notes.zeroHoldsGrown':
      '{count, plural, one {A zero-length hold became a hold of one step} other {# zero-length holds became holds of one step}} (1/{div} beat).',

    // Lines
    'lines.badPosition': 'The position must be finite numbers.',
    'lines.badTime': 'The time to keep the line in place at is not a valid time.',
    'lines.parentCycle': '"{line}" cannot follow "{parent}": that would make "{line}" its own ancestor.',
    'lines.keepPlaceAnimated':
      '"{line}" moves over time, so only its keyframe at {beat} was converted to keep it in place. Check the motion around that keyframe.',
    'lines.unlinkedForEdit':
      'Linked segments around the changed keyframe were unlinked, so their partners stay as they were.',
    'lines.uiMoverNotes':
      'Move {count, plural, one {its note} other {its # notes}} to another line first: notes on a line that moves a display element are never drawn.',
    'lines.uiElementRequired': 'Choose the display element this line should move.',
    'lines.uiMoverAlphaAdded':
      'Opacity 100 % was added to "{line}": without an opacity keyframe the display element would be invisible in Phira.',
    'lines.textureRequired': 'Choose an image for this line.',
    'lines.notImage': '"{path}" is not an image.',
    'lines.notGif': '"{path}" is not a GIF Phira can play.',
    'lines.gifAutoLoopNeedsGif': 'Play automatically needs a GIF image as the line texture.',
    'lines.gifDataRemoved':
      'The GIF playback of "{line}" was removed: its texture is no longer a GIF, and Phira cannot load a chart with GIF playback on another image type.',
    'lines.orderInvalid': 'The new line order must list existing lines, each once.',
    'lines.zOrderRange': 'Draw order must be a whole number between -2147483648 and 2147483647.',
    'lines.countRange': 'Create between 1 and {max} lines at a time.',
    'lines.controlEmpty': 'A distance effect needs at least one point.',
    'lines.controlValue': 'Every distance effect point needs finite numbers.',
    'lines.controlEasing': 'Distance effect points use the RPE curve numbers 0 to 255.',
    'lines.controlOrder': 'Distance effect points must be sorted by distance.',
    'lines.inclineInvalid': 'Note tilt must be a finite number of degrees.',
    'lines.inclineReplaced':
      'The animated note tilt was replaced by a single value: Phira only uses the tilt at the start of the chart.',
    'lines.groupName': 'A group needs a name.',
    'lines.groupOrder':
      'A group position must be a whole number from 1 to {max}. Position 0 is the "Default" group, which holds the lines without a group.',

    // Text segments
    'text.notText': 'This keyframe is not on a text track.',
    'text.noSegment': 'This is the last text keyframe, so no segment starts here. Add a keyframe after it first.',
    'text.emptyText': 'Type on and Erase need some text to show.',
    'text.extendNeedsPrefix': 'Extend / Shrink needs one text to start with the other, and the two to differ.',
    'text.counterNotNumeric':
      'A counter needs plain numbers at both ends. Put a label such as "Score:" on a second text line.',
    'text.easeReplaced':
      'The curve was changed to Linear: Phira crashes when typing or erasing text with a curve that goes past its start or end.',
    'text.unlinked': 'The text segment was unlinked from its partners, so they keep their own texts.',

    // Tempo and timing
    'tempo.bpmInvalid': 'The tempo must be a number of beats per minute greater than 0.',
    'tempo.beforeStart': 'A tempo change must come after beat 0. Change the tempo at beat 0 instead.',
    'tempo.pointExists': 'There is already a tempo change at {beat}.',
    'tempo.firstPointFixed': 'The tempo point at beat 0 cannot move; only its tempo can change.',
    'tempo.firstPointDelete': 'The tempo point at beat 0 cannot be deleted; change its tempo instead.',
    'tempo.pointsCollide': 'With "keep time", this tempo change would land on another tempo change.',
    'tempo.outOfRange': 'This change would move some items outside the range of beats a chart can store.',
    'tempo.snapped':
      'Re-timed {retimed, plural, one {# item} other {# items}}; {snapped, plural, one {# position was} other {# positions were}} rounded to 1/1920 beat{nudged, plural, =0 {.} one {, # of them one step further to stay after the item before it.} other {, # of them one step further to stay after the items before them.}}',
    'timing.offsetInvalid': 'The offset must be a finite number of milliseconds.',
    'timing.folded': "Phira's per-chart adjustment of {ms} ms was moved into the chart offset.",
    'timing.meterInvalid':
      'Each meter mark needs its own beat at or after beat 0 and a number of beats per bar greater than 0.',
    'timing.meterStartAdded':
      'Bar lines are counted from beat 0, so a meter mark of {beatsPerBar, plural, one {# beat} other {# beats}} per bar was added there.',
    'markers.beforeStart': 'Markers cannot be placed before beat 0.',

    // Chart details and package
    'meta.previewStart': 'The song preview must start at 0 s or later.',
    'meta.previewEnd': 'The song preview must end after it starts.',
    'meta.difficulty': 'The difficulty must be a number of 0 or more.',
    'meta.rpeId': 'The RPE chart id may contain only digits.',
    'meta.tags': 'Tags must be text.',
    'pkg.invalid': PKG_FIELD,
    'speed.ignoredDropped':
      '{count, plural, one {# speed curve that Phira ignored was removed} other {# speed curves that Phira ignored were removed}}; the notes move as before.',
    'speed.ignoredApplied':
      '{count, plural, one {# speed curve that Phira used to ignore now takes effect} other {# speed curves that Phira used to ignore now take effect}}.',
    'speed.ignoredSkipped':
      '{count, plural, one {# speed curve could not be applied} other {# speed curves could not be applied}} (custom curves would crash Phira) and stayed linear.',
    'problems.badKey': 'This problem cannot be ignored: it has no id.',
    'export.badRecord': 'The export record is incomplete.',

    // Assets
    'assets.badId': 'The file has no content id.',
    'assets.badSize': 'The file size is not valid.',
    'assets.renamed': 'The file "{from}" was added as "{to}" so Phira can load it.',
    'assets.inUse':
      '"{path}" is used {count, plural, one {once} other {# times}} in the chart. Remove those uses first, or remove the file together with them.',
    'assets.referencesRemoved':
      '{count, plural, one {# use} other {# uses}} of "{path}" were removed with the file (effects and videos using it were deleted).',
    'assets.nonAscii': 'File names in a chart package may use only plain ASCII letters, digits and symbols.',
    'assets.badPath': `"{path}" cannot be used as a file name: ${PATH_REASON}.`,
    'assets.pathTaken': 'Another file in the project is already called "{path}".',
    'assets.notAudio': '"{path}" is not a sound file.',
    'assets.builtinHitsoundName':
      '"{path}" cannot be a custom hit sound: for a file with this name Phira plays its own built-in sound instead. Rename the file first, for example into a folder such as "sfx/{path}".',

    // Effects and videos
    'effects.range': 'An effect must end after it starts.',
    'effects.notShader': '"{path}" is not a shader file.',
    'effects.unknownShader': 'Phira has no built-in effect called "{name}".',
    'effects.varName':
      '"{name}" cannot be a shader variable name: use letters, digits and "_", not starting with a digit.',
    'effects.varValue': 'The value of "{name}" does not match its type.',
    'effects.varTrack': 'The keyframes of "{name}" do not belong to this effect.',
    'effects.orderInvalid': 'The new effect order must list existing effects, each once.',
    'videos.notVideo': '"{path}" is not a video.',
    'videos.scale': 'Choose how the video fills the screen: crop to fill, fit inside, or stretch.',
    'videos.varValue':
      'The {which, select, alpha {opacity} dim {dim} other {{which}}} of a video must be a finite number.',
    'videos.varTrack':
      'The {which, select, alpha {opacity} dim {dim} other {{which}}} keyframes do not belong to this video.',

    // Merge
    'merge.noLines': 'Choose at least one line to merge.',
    'merge.lineMissing': 'The chart to merge has no line {id}.',
    'merge.negativeOffset': 'The merged lines must start at beat 0 or later.',
    'merge.outOfRange': 'The merged lines would end up outside the range of beats a chart can store.',
    'merge.parentsDetached':
      '{count, plural, one {# merged line lost its parent} other {# merged lines lost their parents}}: the parent was not merged.',
    'merge.leadIns':
      '{count, plural, one {# merged property} other {# merged properties}} now start later than beat 0 and hold a neutral value before.',
    'merge.align': 'Choose how the merged lines are placed in time: by beats, by song time, or automatically.',
    'merge.offsetDiffers':
      "Both charts use the same tempo, so the merged notes kept their beats and stay on the beat grid. The charts' offsets differ, so they now play {ms, number} ms {direction, select, later {later} other {earlier}} than in their own chart. (Charts with different tempos are merged by song time instead, which keeps their timing.) If they sound off, shift them in time or merge them by song time.",
    'merge.tempoDiffers':
      'The merged notes kept their beats, but the charts use different tempos, so they play at other times than in their own chart. Merge by song time to keep their timing.',
    'merge.snapped':
      "{count, plural, one {# position was} other {# positions were}} rounded to 1/1920 beat to fit this chart's tempo{nudged, plural, =0 {.} one {, # of them one step further to stay after the item before it.} other {, # of them one step further to stay after the items before them.}}",
  },
};

export default catalog;
